import React, { useState, useRef, useEffect } from 'react';

/**
 * Editable document name field with simplified state logic
 * Pixel-perfect match to InputField.js design
 * Uses Tailwind utility + arbitrary values for easy theming
 * 
 * UPDATED: Uses textarea when editing to allow text wrapping for long names
 */
const EditableDocumentName = ({ 
  value, 
  onSave, 
  isSelected = false, 
  isProcessing = false,
  isIndex = false,
  isError = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => setEditValue(value), [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
      
      // Auto-resize textarea to fit content
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }
  }, [isEditing]);

  const handleClick = (e) => {
    if (isProcessing || isIndex) return;
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else setEditValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent newline in textarea
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const handleChange = (e) => {
    setEditValue(e.target.value);
    
    // Auto-resize textarea as user types
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  // Determine unified visual state
  const visualState =
    isEditing ? 'editing' :
    (isHovered && !isProcessing && !isIndex) ? 'hovered' :
    'default';

  return (
    <div 
      className="h-full flex items-center min-w-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`
          relative flex items-center rounded-app-xs px-app-xs min-w-0 max-w-full
          ${isEditing ? 'w-full h-auto py-[3px] my-app-sm' : 'w-fit h-[24px]'}
          ${isIndex || isProcessing ? '' : 'cursor-text'}
          ${visualState === 'default' ? 'bg-transparent ring-0 transition-none' : ''}
          ${visualState === 'hovered' ? 'bg-transparent ring-1 ring-app-gray-400 transition-none' : ''}
          ${visualState === 'editing' ? 'bg-app-gray-300 ring-2 ring-app-gray-900 transition-colors transition-shadow duration-app-normal' : ''}
        `}
        onClick={!isEditing ? handleClick : undefined}
      >
        {isEditing ? (
          <textarea
            ref={inputRef}
            value={editValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            rows={1}
            className="
              w-full
              bg-transparent outline-none border-none text-app-sm text-app-gray-900 z-5
              resize-none overflow-hidden leading-[18px]
            "
            style={{
              minWidth: 0,
              maxWidth: '100%',
            }}
          />
        ) : (
          <span
            className={`
              relative z-5 text-app-sm overflow-hidden truncate w-fit
              ${isError ? 'text-app-warning-800' : (isProcessing ? 'text-app-gray-500' : 'text-app-gray-900')}
              ${isSelected ? 'font-semibold' : 'font-normal'}
            `}
            style={{ lineHeight: '24px' }}
          >
            {value}
          </span>
        )}
      </div>
    </div>
  );
};

export default EditableDocumentName;